/* eslint-disable @typescript-eslint/naming-convention */
// Some names in this module are prescribed for us by the
// Design Team's tokens' names.  Let's ignore this rule just this once.
import tokens from 'airship-design-system/tokens.json'

import {FixMeAny} from 'airship/types'

export interface PaletteValues {
  '10': string
  '20': string
  '30': string
  '40': string
  '50': string
  '60': string
  '70': string
  '80': string
  '90': string
  '100': string
  '10-hover': string
  '20-hover': string
  '30-hover': string
  '40-hover': string
  '50-hover': string
  '60-hover': string
  '70-hover': string
  '80-hover': string
  '90-hover': string
  '100-hover': string
}

type PaletteColor = Exclude<keyof typeof tokens.palette, 'base'>
type PaletteColorMap = {
  [key in PaletteColor]: PaletteValues
}

type BaseColor = keyof typeof tokens.palette.base
type BaseColorMap = {
  [key in BaseColor]: string
}

/* Transforms this:

    "palette": {
      "base": {
        "black": {
          "value": "#101113",
          "type": "color"
        },
        "black-hover": {
          "value": "#1b1c20",
          "type": "color",
          "description": "#16181b"
        },
        "white": {
          "value": "#ffffff",
          "type": "color"
        },
        "white-hover": {
          "value": "#eff0f5",
          "type": "color"
        }
      },
      "gray": {
        "10": {
          "value": "#f7f8fa",
          "type": "color"
        },
        "20": {
          "value": "#eceef2",
          "type": "color"
        },
        ...
      },
      "other-colors": {
        ...
      }
      ...
    }

  Into:

    "base": {
      "black": "#101113"
    },
    "gray": {
      "10": "#f7f8fa",
      "20": "#eceef2",
      ...
    },
    ...

  Note that `base` has totally different keys from the keys named after colors.

*/
const paletteColorMap = Object.keys(tokens.palette).reduce((obj, key) => {
  obj[key as PaletteColor] = Object.keys(
    tokens.palette[key as PaletteColor]
  ).reduce((innerObj, innerKey) => {
    innerObj[innerKey as keyof PaletteValues] =
      tokens.palette[key as PaletteColor][innerKey as keyof PaletteValues].value
    return innerObj
  }, {} as PaletteValues)
  return obj
}, {} as PaletteColorMap)

const paletteColors = {
  base: Object.keys(tokens.palette.base).reduce((obj, key) => {
    obj[key as BaseColor] = tokens.palette.base[key as BaseColor].value
    return obj
  }, {} as BaseColorMap),
  ...paletteColorMap,
}

type TextColorContext = keyof typeof tokens.color.text
type TextAccentColorContext = keyof typeof tokens.color.text.accent
type BaseTextColor = {
  [Property in Exclude<TextColorContext, 'accent'>]?: string
}
type TextAccentColor = {[Property in TextAccentColorContext]?: string}
type TextColor = BaseTextColor & {accent: TextAccentColor}

type BackgroundColorContext = keyof typeof tokens.color.background
type BackgroundAccentColorContext = keyof typeof tokens.color.background.accent
type BaseBackgroundColor = {
  [Property in Exclude<BackgroundColorContext, 'accent'>]?: string
}
type BackgroundAccentColor = {
  [Property in BackgroundAccentColorContext]?: string
}
type BackgroundColor = BaseBackgroundColor & {accent: BackgroundAccentColor}

type IconColorContext = keyof typeof tokens.color.icon
type IconAccentColorContext = keyof typeof tokens.color.icon.accent
type BaseIconColor = {
  [Property in Exclude<IconColorContext, 'accent'>]?: string
}
type IconAccentColor = {[Property in IconAccentColorContext]?: string}
type IconColor = BaseIconColor & {accent: IconAccentColor}

type BorderColorContext = keyof typeof tokens.color.border
type BorderAccentColorContext = keyof typeof tokens.color.border.accent
type BaseBorderColor = {
  [Property in Exclude<BorderColorContext, 'accent'>]?: string
}
type BorderAccentColor = {
  [Property in BorderAccentColorContext]?: string
}
type BorderColor = BaseBorderColor & {accent: BorderAccentColor}

type FocusColorContext = keyof typeof tokens.color.focus
type FocusColor = {[Property in FocusColorContext]: string}

export interface Colors {
  text: TextColor
  background: BackgroundColor
  icon: IconColor
  border: BorderColor
  focus: FocusColor
}

type ColorValueType = {
  type: 'color'
  value: string
}
const isColorValue = (value: FixMeAny): value is ColorValueType => {
  return (
    (value && (value as ColorValueType)).value !== undefined &&
    (value && (value as ColorValueType)).type === 'color'
  )
}

const isAccentColorValue = (
  value: FixMeAny
): value is Record<TextAccentColorContext, ColorValueType> => {
  return (
    (value && (value as Record<TextAccentColorContext, ColorValueType>)).blue
      .value !== undefined
  )
}

/* Transform this:

  "color": {
    "text": {
      "primary": {
        "value": "#131721",
        "type": "color"
      },
      "primary-hover": {
        "value": "#1b2130",
        "type": "color"
      },
      ...
      "accent": {
        "blue": {
          "value": "#1a48d6",
          "type": "color"
        }
      }
    }
    ...

  Into:

    "text": {
      "primary": "#131721",
      "primary-hover": "#1b2130",
      "accent": {
        "blue": "#1a48d6",
        ...
      }
      ...
    }
    ...

*/
const colors: Colors = {
  text: Object.keys(tokens.color.text).reduce((obj, key) => {
    const colorValue = tokens.color.text[key as TextColorContext]
    if (isColorValue(colorValue)) {
      obj[key as TextColorContext] = colorValue.value
    } else if (isAccentColorValue(colorValue)) {
      obj.accent = Object.keys(colorValue).reduce((innerObj, innerKey) => {
        const accents = tokens.color.text.accent
        innerObj[innerKey as TextAccentColorContext] =
          accents[innerKey as TextAccentColorContext].value
        return innerObj
      }, {} as TextAccentColor)
    }
    return obj
  }, {} as TextColor),
  background: Object.keys(tokens.color.background).reduce((obj, key) => {
    const colorValue = tokens.color.background[key as BackgroundColorContext]
    if (isColorValue(colorValue)) {
      obj[key as BackgroundColorContext] = colorValue.value
    } else if (isAccentColorValue(colorValue)) {
      obj.accent = Object.keys(colorValue).reduce((innerObj, innerKey) => {
        const accents = tokens.color.background.accent
        innerObj[innerKey as BackgroundAccentColorContext] =
          accents[innerKey as BackgroundAccentColorContext].value
        return innerObj
      }, {} as BackgroundAccentColor)
    }
    return obj
  }, {} as BackgroundColor),
  icon: Object.keys(tokens.color.icon).reduce((obj, key) => {
    const colorValue = tokens.color.icon[key as IconColorContext]
    if (isColorValue(colorValue)) {
      obj[key as IconColorContext] = colorValue.value
    } else if (isAccentColorValue(colorValue)) {
      obj.accent = Object.keys(colorValue).reduce((innerObj, innerKey) => {
        const accents = tokens.color.background.accent
        innerObj[innerKey as IconAccentColorContext] =
          accents[innerKey as IconAccentColorContext].value
        return innerObj
      }, {} as IconAccentColor)
    }
    return obj
  }, {} as IconColor),
  border: Object.keys(tokens.color.border).reduce((obj, key) => {
    const colorValue = tokens.color.border[key as BorderColorContext]
    if (isColorValue(colorValue)) {
      obj[key as BorderColorContext] = colorValue.value
    } else if (isAccentColorValue(colorValue)) {
      obj.accent = Object.keys(colorValue).reduce((innerObj, innerKey) => {
        const accents = tokens.color.background.accent
        innerObj[innerKey as BorderAccentColorContext] =
          accents[innerKey as BorderAccentColorContext].value
        return innerObj
      }, {} as BorderAccentColor)
    }
    return obj
  }, {} as BorderColor),
  focus: Object.keys(tokens.color.focus).reduce(
    (obj, key) => {
      obj[key as FocusColorContext] =
        tokens.color.focus[key as FocusColorContext].value
      return obj
    },
    {} as Record<FocusColorContext, string>
  ),
  ...paletteColors,
}

export default colors
